import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, ShieldCheck, Home, Landmark, Users, PercentIcon, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { LOGO_URL } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => n == null ? "—" : Number(n).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export default function ClientPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [approving, setApproving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", comments: "" });

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/portal/${token}`);
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Link invalid or revoked");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const approve = async (e) => {
    e.preventDefault();
    setApproving(true);
    try {
      await axios.post(`${API}/portal/${token}/approve`, form);
      toast.success("Business terms approved");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setApproving(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-8" data-testid="portal-error">
        <div className="grid-panel p-10 max-w-md text-center">
          <AlertTriangle size={36} className="mx-auto text-amber-600 mb-3" />
          <div className="kicker mb-2">Unavailable</div>
          <h1 className="text-2xl font-display">{error}</h1>
          <p className="text-sm text-slate-500 mt-3">Please contact your broker for a new link.</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Loading…</div>;

  const { share, transaction: t } = data;

  return (
    <div className="min-h-screen bg-background" data-testid="client-portal-page">
      <div className="bg-navy text-white">
        <div className="max-w-4xl mx-auto px-8 py-8">
          <div className="flex items-center justify-between">
            <img src={LOGO_URL} alt="Moaglee Commercial Real Estate" className="max-h-24 w-auto" />
            {share.approved && (
              <div className="text-right">
                <div className="kicker text-brass">Approved</div>
                <div className="text-sm">{share.approved_by_name}</div>
                <div className="text-[11px] text-white/60">{share.approved_at?.slice(0, 10)}</div>
              </div>
            )}
          </div>
          <div className="mt-8">
            <div className="kicker text-brass mb-2">Business Terms · For Your Review</div>
            <h1 className="text-3xl font-display">{t.name}</h1>
            <div className="text-sm text-white/60 font-mono mt-1">{t.property_data?.address}</div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-8 py-8 space-y-6">
        <Section icon={Home} title="Property">
          <Row label="Address" value={t.property_data?.address} />
          <Row label="Type" value={t.property_data?.property_type || "—"} />
          <Row label="Parcel" value={t.property_data?.parcel_number || "—"} />
          <Row label="Zoning" value={t.property_data?.zoning || "—"} last />
        </Section>

        <Section icon={Users} title="Buyer">
          <Row label="Buyer" value={t.buyer_data?.name} />
          <Row label="Entity" value={t.buyer_data?.buyer_entity || "—"} />
          <Row label="Authorized Signer" value={t.buyer_data?.authorized_signer || "—"} last />
        </Section>

        <Section icon={Landmark} title="Offer">
          <Row label="Offer Price" value={money(t.offer_terms?.offer_price)} mono />
          <Row label="Earnest Money" value={money(t.offer_terms?.earnest_money)} mono />
          <Row label="Feasibility Period" value={t.offer_terms?.feasibility_period_days ? `${t.offer_terms.feasibility_period_days} days` : "—"} />
          <Row label="Closing" value={t.offer_terms?.closing_date || "—"} />
          <Row label="Escrow" value={t.offer_terms?.escrow_company || "—"} />
          <Row label="Title" value={t.offer_terms?.title_company || "—"} last />
        </Section>

        {t.financing_terms?.seller_financing && (
          <Section icon={PercentIcon} title="Seller Financing">
            <Row label="Purchase Price" value={money(t.financing_terms?.purchase_price)} mono />
            <Row label="Down Payment" value={money(t.financing_terms?.down_payment)} mono />
            <Row label="Financed Principal" value={money(t.financing_terms?.financed_principal)} mono />
            <Row label="Interest Rate" value={t.financing_terms?.interest_rate != null ? `${t.financing_terms.interest_rate}%` : "—"} />
            <Row label="Amortization" value={t.financing_terms?.amortization_years ? `${t.financing_terms.amortization_years} years` : "—"} last />
          </Section>
        )}

        <Section icon={ShieldCheck} title="Commission">
          <Row label="Commission %" value={t.commission_terms?.commission_percentage != null ? `${t.commission_terms.commission_percentage}%` : "—"} />
          <Row label="Paying Party" value={t.commission_terms?.paying_party || "—"} />
          <Row label="Included in Offer" value={t.commission_terms?.included_in_offer ? "Yes" : t.commission_terms?.included_in_offer === false ? "No" : "—"}
            last />
        </Section>

        {t.facts?.length > 0 && (
          <Section icon={ShieldCheck} title={`Broker-Confirmed Facts (${t.facts.length})`}>
            <div className="divide-y divide-border -mx-4">
              {t.facts.map((f) => (
                <div key={f.id} className="px-4 py-3 flex items-start justify-between gap-4">
                  <div className="text-sm">
                    <div className="font-medium">{f.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Source: {f.source_url ? <a href={f.source_url} target="_blank" rel="noreferrer" className="link-brass">{f.source_name}</a> : (f.source_name || "—")}
                    </div>
                  </div>
                  <div className="text-sm font-mono text-navy">{f.value || "—"}</div>
                </div>
              ))}
            </div>
          </Section>
        )}

        <div className="grid-panel p-5 bg-slate-50 border-l-4 border-brass">
          <p className="text-sm text-slate-700 leading-relaxed">
            <strong>Nonbinding notice:</strong> This document reflects business terms only.
            Nothing here constitutes a title commitment, legal opinion, appraisal, or guaranteed
            legal fact. Business terms are subject to broker, client, title, lender, tax, and legal
            review, as applicable.
          </p>
        </div>

        {share.allow_approval && !share.approved && (
          <form onSubmit={approve} className="grid-panel p-6" data-testid="approve-form">
            <div className="kicker mb-3">Approve business terms</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input required placeholder="Your full name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="approve-name"
                className="bg-white border border-border px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-brass" />
              <input required type="email" placeholder="Your email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="approve-email"
                className="bg-white border border-border px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-brass" />
            </div>
            <textarea placeholder="Comments (optional)" rows={3} value={form.comments}
              onChange={(e) => setForm({ ...form, comments: e.target.value })}
              data-testid="approve-comments"
              className="mt-4 w-full bg-white border border-border px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-brass" />
            <button type="submit" disabled={approving} className="btn-brass mt-4" data-testid="approve-submit">
              <ShieldCheck size={14} /> {approving ? "Submitting…" : "Approve Business Terms"}
            </button>
          </form>
        )}

        {share.approved && (
          <div className="grid-panel p-6 bg-emerald-50 border-l-4 border-emerald-600 flex items-start gap-3" data-testid="approved-banner">
            <CheckCircle2 size={20} className="text-emerald-700 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-emerald-900">Approved by {share.approved_by_name}</div>
              <div className="text-xs text-emerald-800 mt-1">{share.approved_at}</div>
            </div>
          </div>
        )}
      </div>
      <Toaster richColors position="top-right" />
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section className="grid-panel">
      <div className="border-b border-border px-5 py-3 flex items-center gap-2">
        <Icon size={14} className="text-brass" />
        <div className="kicker">{title}</div>
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Row({ label, value, mono, last }) {
  return (
    <div className={`flex items-center justify-between py-2 ${!last ? "border-b border-border" : ""}`}>
      <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-sm ${mono ? "font-mono" : ""} text-navy`}>{value}</div>
    </div>
  );
}
